// Historical backtest — serve mirrored backtest_margins payment_factor;
// scale to farmer protection. Never invent rows; never mix vintages.

import { capHarvestPrice, roundHalfUp } from "./mcoModel.js";
import { num, numOrNull, toShareDecimal } from "./mcoShared.js";

export const BACKTEST_VINTAGES = [
  { code: "27-MCO", label: "27-MCO (default — product on sale)" },
  { code: "26-MCO", label: "26-MCO (2026 rules / audit)" },
];

const round2 = (v) => roundHalfUp(v, 2);

/**
 * Filter rows to a practice (irrigated / non_irrigated), then keep one row per year
 * (prefer the matching practice; else first seen). Shared by MCO and ECO backtest rows —
 * both `backtest_margins` and `eco_backtest_comparison` carry `year` + `practice`.
 * @param {object[]} rows
 * @param {string} [practiceFilter]
 * @returns {Map<number, object>}
 */
export function groupBacktestRowsByYear(rows, practiceFilter = "") {
  const list = Array.isArray(rows) ? rows : [];
  let filtered = list;
  const want = String(practiceFilter || "").toLowerCase();
  if (want.includes("non")) {
    filtered = list.filter((r) =>
      String(r.practice || "")
        .toLowerCase()
        .includes("non")
    );
  } else if (want.includes("irrig")) {
    filtered = list.filter(
      (r) =>
        String(r.practice || "")
          .toLowerCase()
          .includes("irrig") &&
        !String(r.practice || "")
          .toLowerCase()
          .includes("non")
    );
  }
  if (!filtered.length) filtered = list;

  const byYear = new Map();
  for (const row of filtered) {
    const y = Number(row.year);
    if (!Number.isFinite(y)) continue;
    if (!byYear.has(y)) byYear.set(y, row);
  }
  return byYear;
}

/**
 * Farmer $/ac indemnity from a backtest row's payment_factor + farmer inputs.
 * Serves row.payment_factor (does not recompute PF).
 */
export function farmerIndemnityFromBacktest(row, farmer = {}) {
  if (!row) {
    return { ready: false, blockers: ["backtest row missing"], result: null };
  }

  const pf = num(row.payment_factor ?? row.paymentFactor);
  const pp = num(row.mpp_projected ?? row.mppProjected);
  const hpRaw = num(row.mpp_harvest ?? row.mppHarvest);
  const cr = num(row.coverage_range ?? row.coverageRange);
  const planCode = String(row.insurance_plan_code ?? row.insurancePlanCode ?? farmer.planCode ?? "68");
  const approvedYield = num(farmer.approvedYield);
  const protectionFactor = num(farmer.protectionFactor ?? 1);
  const share = toShareDecimal(farmer.sharePct);
  const acres = num(farmer.acres ?? 1);

  const blockers = [];
  if (!(pf >= 0) || Number.isNaN(pf)) blockers.push("payment_factor missing");
  if (!(pp > 0)) blockers.push("mpp_projected missing");
  if (!(cr > 0)) blockers.push("coverage_range missing");
  if (!(approvedYield > 0)) blockers.push("approved yield (APH) missing");
  if (!(protectionFactor > 0)) blockers.push("protection factor missing");
  if (blockers.length) return { ready: false, blockers, result: null };

  const hp = capHarvestPrice(hpRaw > 0 ? hpRaw : pp, pp);
  const ecvPrice = planCode === "68" ? Math.max(pp, hp) : pp;
  const protectionPerAcre = round2(approvedYield * ecvPrice * cr * protectionFactor * share);
  // Spec: indemnity = round_to_whole(PF × protection). Per-acre display uses
  // PF × protectionPerAcre; total uses whole-dollar of full protection.
  const indemnityPerAcre = round2(pf * protectionPerAcre);
  const protectionTotal = protectionPerAcre * (acres > 0 ? acres : 1);
  const indemnityTotal = Math.round(pf * protectionTotal);

  return {
    ready: true,
    blockers: [],
    result: {
      year: Number(row.year),
      paymentFactor: pf,
      protectionPerAcre,
      indemnityPerAcre,
      indemnityTotal,
      acres: acres > 0 ? acres : 1,
      projectedPrice: pp,
      harvestPrice: hp,
      eay: num(row.eay),
      fay: num(row.fay),
      // Display-only, so nullable: an unresolved year has no harvest margin, and printing $0.00
      // for it would say the margin landed at zero — a different claim from "not determined".
      expectedMargin: numOrNull(row.expected_margin ?? row.expectedMargin),
      harvestMargin: numOrNull(row.harvest_margin ?? row.harvestMargin),
      triggerMargin: numOrNull(row.trigger_margin ?? row.triggerMargin),
      illustrativeIndemnityPerAcre: num(row.illustrative_indemnity_per_acre ?? row.illustrativeIndemnityPerAcre),
      endorsementVintage: String(row.endorsement_vintage ?? row.endorsementVintage ?? ""),
      potashProvenance: String(row.potash_provenance ?? row.potashProvenance ?? ""),
      practice: String(row.practice ?? ""),
      planCode,
      yieldSource: String(row.yield_source ?? row.yieldSource ?? ""),
      potashSource: String(row.potash_source ?? row.potashSource ?? ""),
      disclaimer: String(row.backtest_disclaimer ?? row.backtestDisclaimer ?? ""),
      vintageNote: String(row.vintage_note ?? row.vintageNote ?? ""),
    },
  };
}

/**
 * Build year table for one vintage. Groups by year (one practice row preferred).
 * @param {object[]} rows — mirror BacktestMargins rows (single vintage)
 * @param {object} farmer
 * @param {string} [practiceFilter] — irrigated | non_irrigated
 */
export function buildBacktestReport(rows, farmer = {}, practiceFilter = "") {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) {
    return {
      ready: false,
      blockers: ["No backtest rows for this county / crop / plan / vintage"],
      years: [],
      disclaimer: null,
    };
  }

  const vintages = new Set(list.map((r) => String(r.endorsement_vintage ?? r.endorsementVintage ?? "")));
  if (vintages.size > 1) {
    return {
      ready: false,
      blockers: ["Mixed endorsement vintages — refuse to aggregate"],
      years: [],
      disclaimer: null,
    };
  }

  const byYear = groupBacktestRowsByYear(list, practiceFilter);

  const years = [...byYear.keys()]
    .sort((a, b) => a - b)
    .map((y) => {
      const applied = farmerIndemnityFromBacktest(byYear.get(y), farmer);
      return {
        year: y,
        ready: applied.ready,
        blockers: applied.blockers,
        ...(applied.result || {}),
      };
    });

  const anyReady = years.some((y) => y.ready);
  const sample = years.find((y) => y.ready) || years[0];

  return {
    ready: anyReady,
    blockers: anyReady ? [] : [...new Set(years.flatMap((y) => y.blockers || []))],
    years,
    disclaimer: sample?.disclaimer || null,
    vintageNote: sample?.vintageNote || null,
    potashSource: sample?.potashSource || null,
    yieldSource: sample?.yieldSource || null,
    endorsementVintage: sample?.endorsementVintage || [...vintages][0] || "",
  };
}
